import { Controller } from "@hotwired/stimulus"
import loadYmaps from "../../src/shared/helpers/load_ymaps"

export default class extends Controller {
  static targets = ["map"]
  static values = { clusters: String, focus: String, standaloneCities: String }

  connect() {
    console.log("City clusters controller connected")
    console.log("Focus value:", this.focusValue)

    try {
      if (!this.clustersValue || this.clustersValue === 'null' || this.clustersValue === 'undefined') {
        console.error("No valid clusters data provided")
        this.parsedClusters = []
      } else {
        // Try to parse the JSON
        this.parsedClusters = JSON.parse(this.clustersValue)
        console.log("Parsed clusters:", this.parsedClusters.length, "clusters")
      }

      if (!this.hasStandaloneCitiesValue || !this.standaloneCitiesValue || this.standaloneCitiesValue === 'null' || this.standaloneCitiesValue === 'undefined') {
        this.parsedStandaloneCities = []
      } else {
        this.parsedStandaloneCities = JSON.parse(this.standaloneCitiesValue)
        console.log("Parsed standalone cities:", this.parsedStandaloneCities.length)
      }

      this.initializeMap()
    } catch (error) {
      console.error("Error parsing map data:", error)
      this.parsedClusters = []
      this.parsedStandaloneCities = []
      this.initializeMap()
    }
  }

  initializeMap() {
    loadYmaps(() => {
      ymaps.ready(() => {
        // Determine map center and zoom based on clusters
        const mapConfig = this.getMapConfig()

        const map = new ymaps.Map(this.element, {
          center: mapConfig.center,
          zoom: mapConfig.zoom,
          controls: ['zoomControl', 'fullscreenControl']
        })

        this.addClustersToMap(map)
      })
    })
  }

  getMapConfig() {
    if (this.focusValue === 'all') {
      console.log("Focus on all clusters (index page)")
      // Multiple clusters view (index page) - default center
      return {
        center: [55.76, 37.64], // Moscow
        zoom: 5
      }
    } else {
      // Focus on specific cluster (edit page) - find the cluster by ID
      const clusterId = parseInt(this.focusValue)
      const cluster = this.parsedClusters.find(c => c.id === clusterId) || this.parsedClusters[0]
      console.log("Focus on cluster:", clusterId, cluster)

      return {
        center: [cluster.center_lat, cluster.center_lng],
        zoom: 10 // Initial zoom, will be adjusted by setBounds
      }
    }
  }



  addClustersToMap(map) {
    const allPlacemarks = []
    const allLines = []

    this.parsedClusters.forEach(cluster => {
      // Add cluster center placemark
      const centerPlacemark = new ymaps.Placemark(
        [cluster.center_lat, cluster.center_lng],
        {
          balloonContentHeader: `${cluster.center_name} (Центр кластера)`,
          balloonContentBody: this.getClusterCenterBalloonContent(cluster),
          hintContent: `${cluster.center_name} - центр кластера (${cluster.cities_count} городов)`
        },
        {
          preset: 'islands#redDotIcon',
          iconColor: '#ff5722',
          iconContent: cluster.cities_count.toString()
        }
      )
      allPlacemarks.push(centerPlacemark)

      // Add city placemarks and connection lines
      cluster.cities.forEach(city => {
        if (!city.is_center) { // Skip center city as it's already added
          const cityPlacemark = new ymaps.Placemark(
            [city.lat, city.lng],
            {
              balloonContentHeader: `${city.name}`,
              balloonContentBody: this.getCityBalloonContent(city, cluster),
              hintContent: `${city.name} (${city.tech_placements_count} размещений)`
            },
            {
              preset: 'islands#blueDotIcon',
              iconColor: '#1e88e5'
            }
          )
          allPlacemarks.push(cityPlacemark)

          // Add connection line from city to cluster center
          // Use blue for focused cluster, green for others
          const isFocusedCluster = this.focusValue !== 'all' && cluster.id === parseInt(this.focusValue)
          const lineColor = isFocusedCluster ? '#FF5733' : '#00aa00'

          const line = new ymaps.Polyline(
            [
              [city.lat, city.lng],
              [cluster.center_lat, cluster.center_lng]
            ],
            {
              balloonContent: `Связь с центром кластера: ${cluster.center_name}`
            },
            {
              strokeColor: lineColor,
              strokeWidth: 2,
              strokeOpacity: 0.6
            }
          )
          allLines.push(line)
        }
      })
    })


    if (this.parsedStandaloneCities && this.parsedStandaloneCities.length > 0) {
      this.parsedStandaloneCities.forEach(city => {
        const cityPlacemark = new ymaps.Placemark(
          [city.lat, city.lng],
          {
            balloonContentHeader: `${city.name}`,
            balloonContentBody: this.getStandaloneCityBalloonContent(city),
            hintContent: `${city.name} (${city.tech_placements_count} размещений)`
          },
          {
            preset: 'islands#yellowDotIcon',
            iconColor: '#fbc02d'
          }
        )
        allPlacemarks.push(cityPlacemark)
      })
    }

    // Add all placemarks to a clusterer for multiple clusters, or directly for single cluster focus
    if (this.focusValue === 'all') {
      // For multiple clusters, use clusterer
      const clusterer = new ymaps.Clusterer({
        preset: 'islands#invertedVioletClusterIcons',
        groupByCoordinates: false,
        clusterDisableClickZoom: false,
        clusterHideIconOnBalloonOpen: false,
        geoObjectHideIconOnBalloonOpen: false
      })

      clusterer.add(allPlacemarks)
      map.geoObjects.add(clusterer)
    } else {
      // For single cluster focus, add placemarks directly without clustering
      allPlacemarks.forEach(placemark => {
        map.geoObjects.add(placemark)
      })
    }

    // Add all connection lines
    allLines.forEach(line => {
      map.geoObjects.add(line)
    })

    // Fit map to show clusters based on focus value
    if (allPlacemarks.length > 0) {
      if (this.focusValue === 'all') {
        // Fit bounds to all placemarks when showing all clusters
        const coordinates = allPlacemarks.map(p => p.geometry.getCoordinates())
        const bounds = [
          [Math.min(...coordinates.map(c => c[0])), Math.min(...coordinates.map(c => c[1]))],
          [Math.max(...coordinates.map(c => c[0])), Math.max(...coordinates.map(c => c[1]))]
        ]
        map.setBounds(bounds, {
          checkZoomRange: true,
          zoomMargin: 20
        })
      } else {
        // For specific cluster focus, fit bounds to only this cluster's cities
        const clusterId = parseInt(this.focusValue)
        const cluster = this.parsedClusters.find(c => c.id === clusterId) || this.parsedClusters[0]

        if (cluster && cluster.cities && cluster.cities.length > 0) {
          // If cluster has only one city (the center), set fixed zoom level
          if (cluster.cities.length === 1) {
            map.setCenter([cluster.center_lat, cluster.center_lng])
            map.setZoom(11)
          } else {
            // For clusters with multiple cities, use bounds fitting
            const coordinates = cluster.cities.map(city => [city.lat, city.lng])
            const bounds = [
              [Math.min(...coordinates.map(c => c[0])), Math.min(...coordinates.map(c => c[1]))],
              [Math.max(...coordinates.map(c => c[0])), Math.max(...coordinates.map(c => c[1]))]
            ]
            map.setBounds(bounds, {
              checkZoomRange: true,
              zoomMargin: 100
            })
          }
        }
      }
    }

    console.log(`Added ${allPlacemarks.length} placemarks and ${allLines.length} connection lines`)
  }

  getClusterCenterBalloonContent(cluster) {
    const editPath = `/admin/city_clusters/${cluster.id}/edit`

    return `
      <strong>Центр кластера:</strong> ${cluster.center_name}<br>
      <strong>Регион:</strong> ${cluster.region_name}<br>
      <strong>Количество городов:</strong> ${cluster.cities_count}<br>
      <strong>Общее количество размещений:</strong> ${cluster.total_placements}<br>
      <br>
      <a href="/admin/cities/${cluster.center_id}/edit" class="btn btn-success btn-sm"><i class="bi bi-pencil"></i> Центр</a>
      <a href="${editPath}" class="btn btn-success btn-sm"><i class="bi bi-pencil"></i> Кластер</a>
    `
  }

  getCityBalloonContent(city, cluster) {
    const createClusterButton = !city.is_center ?
      `<a href="/admin/city_clusters?center_id=${city.id}"
         data-method="post"
         class="btn btn-primary btn-sm"
         data-confirm="Создать новый кластер с центром ${city.name}?">
         <i class="bi bi-plus-circle"></i> Создать кластер
       </a>` : ''

    const addToCurrentClusterButton = city.cluster_id != cluster.id && this.focusValue != 'all' ?
      `<a href="/admin/city_clusters/${this.focusValue}/add_city?city_id=${city.id}"
          class="btn btn-primary btn-sm"
          data-method="patch"
          data-confirm="Добавить город в текущий кластер?">
          <i class="bi bi-plus-circle"></i> Добавить
        </a>` : ''

    return `
      <strong>Город:</strong> ${city.name}<br>
      <strong>Регион:</strong> ${cluster.region_name}<br>
      ${city.region_area ? `<strong>Район:</strong> ${city.region_area}<br>` : ''}
      <strong>Размещений:</strong> ${city.tech_placements_count}<br>
      <strong>Центр кластера:</strong> ${cluster.center_name}<br>
      <br>
      <a href="/admin/cities/${city.id}/edit" class="btn btn-success btn-sm"><i class="bi bi-pencil"></i> Город</a>
      ${createClusterButton}
      ${addToCurrentClusterButton}
    `
  }

  getStandaloneCityBalloonContent(city) {
    const addToCurrentClusterButton = this.focusValue != 'all' ?
      `<a href="/admin/city_clusters/${this.focusValue}/add_city?city_id=${city.id}"
          class="btn btn-primary btn-sm"
          data-method="patch"
          data-confirm="Добавить город в текущий кластер?">
          <i class="bi bi-plus-circle"></i> Добавить
        </a>` : ''


    return `
      <strong>Город:</strong> ${city.name}<br>
      <strong>Регион:</strong> ${city.region_name}<br>
      ${city.region_area ? `<strong>Район:</strong> ${city.region_area}<br>` : ''}
      <strong>Размещений:</strong> ${city.tech_placements_count}<br>
      <br>
      <a href="/admin/cities/${city.id}/edit" class="btn btn-success btn-sm"><i class="bi bi-pencil"></i> Город</a>
      <a href="/admin/city_clusters?center_id=${city.id}"
         data-method="post"
         class="btn btn-primary btn-sm"
         data-confirm="Создать новый кластер с центром ${city.name}?">
         <i class="bi bi-plus-circle"></i> Создать кластер
      </a>
      ${addToCurrentClusterButton}
    `
  }
}
